import { Injectable } from '@angular/core';
import { Auth, User, signOut, authState } from '@angular/fire/auth';
import { signInWithCustomToken } from 'firebase/auth';
import { UserRoleService } from './user-role.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * AuthService - Core authentication and user state management
 * 
 * Responsibilities:
 * - Manage Firebase authentication state
 * - Fetch and cache user role/profile data
 * - Handle authentication errors and communicate via observables
 * - Manage user session state (user$, idToken$, authError$)
 * - Handle post-login redirects and navigation
 * - Coordinate with UserRoleService for backend integration
 * 
 * Used by:
 * - LoginComponent: For error display and basic auth checks
 * - AuthGuard: For route protection decisions
 * - App-wide: For reactive user state via observables
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user = new BehaviorSubject<any | null>(null);
  public readonly user$: Observable<any | null> = this._user.asObservable();

  private _idToken = new BehaviorSubject<string | null>(null);
  public readonly idToken$: Observable<string | null> =
    this._idToken.asObservable();

  private _authError = new BehaviorSubject<string | null>(null);
  public readonly authError$: Observable<string | null> = this._authError.asObservable();

  private isInitialized = false;

  constructor(
    private auth: Auth,
    private router: Router,
    private userRoleService: UserRoleService
  ) {
    // Listen to Firebase auth state changes using Angular Fire
    authState(this.auth).subscribe((user: User | null) => this.handleAuthUserStateChanged(user));
  }

  /**
   * Handle Firebase auth state changes
   */
  private async handleAuthUserStateChanged(user: User | null): Promise<void> {
    this.isInitialized = true;
    
    if (user && user?.email) {
      console.log('🔍 [AuthService] User authenticated, fetching role data');
      await this.getUserRoleByEmailOrPhone(
        user
      );
    } else {
      console.log("👤 [AuthService] No user authenticated, considering as signout flow");
      this._user.next(null);
      this._idToken.next(null);
    }
  }

  /**
   * Get user role by email or phone and process the response
   * - Supports backend returning a customToken (Firebase custom token). If present, we sign-in
   *   with that custom token to establish Firebase session and then continue normal flow.
   */
  private async getUserRoleByEmailOrPhone(
    user: User,
  ): Promise<void> {
    try {
      console.log('🔍 [AuthService] Looking up user role');
      
      const result: any = await this.userRoleService
        .getUserAccountWithErrorHandling()
        .toPromise();

      const returnUrl =
        this.router.routerState.snapshot.root.queryParams['returnUrl'] || '/dashboard';

      // If backend explicitly marks user inactive => block login
      if (result && result.status === 'inactive') {
        console.warn('🚫 [AuthService] User account is inactive, blocking login');
        try {
          await this.signOut(); // Sign out the user (clears firebase session)
        } catch (e) {
          console.warn('⚠️ [AuthService] signOut during inactive handling failed:', e);
        }
        // Emit error instead of navigating
        this._authError.next('Your account has been deactivated. Please contact support.');
        return;
      }

      // If backend provided a idToken for this principal (user or membership),
      // use it to sign in so Firebase session matches backend-issued token/claims.
      if (result && result.idToken) {
        try {
          console.log('🔐 [AuthService] Custom token received from backend, signing in with custom token');
          // signInWithCustomToken accepts the Firebase Auth instance and the token
          await signInWithCustomToken(this.auth as any, result.idToken);
        } catch (err) {
          console.error('❌ [AuthService] Failed to sign in with custom token:', err);
          // Continue without throwing — we'll fall back to existing firebase user (if any)
        }
      }

      // Ensure we take the current firebase user (might have changed after custom token sign-in)
      const firebaseUser = this.auth.currentUser || user;

      // Get Firebase ID token for active users (including those unregistered)
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          this._idToken.next(token);
        } catch (tokenErr) {
          console.error('❌ [AuthService] Error getting ID token:', tokenErr);
          // continue — downstream logic may treat as unauthenticated
          this._idToken.next(null);
        }
      }

      if (result && result.user && result.status === 'active') {
        // User found and active - ensure uid is set for auth guard compatibility
        console.log('✅ [AuthService] Active user found:', result.user.id);
        const userWithUid = {
          ...result.user,
          uid: result.user.id // Map id to uid for auth guard compatibility
        };
        this._user.next(userWithUid);
        this.handlePostLoginRedirect(returnUrl);
      } else if (result && result.status === 'unregistered') {
        // User not found in backend but authenticated in Firebase
        console.warn('⚠️ [AuthService] User unregistered, redirecting to registration');
        
        // Set a minimal user object to indicate Firebase authentication but unregistered status
        this._user.next({
          uid: firebaseUser?.uid,
          firebaseAuth: true,
          registered: false,
          status: 'unregistered'
        });
        this.router.navigate(['/unregistered'], { queryParams: { returnUrl } });
      } else {
        // Fallback case
        console.warn('⚠️ [AuthService] Unexpected status, redirecting to unregistered');
        
        // Set a minimal user object to indicate Firebase authentication but unregistered status
        this._user.next({
          uid: firebaseUser?.uid,
          firebaseAuth: true,
          registered: false,
          status: 'unregistered'
        });
        this.router.navigate(['/unregistered'], { queryParams: { returnUrl } });
      }
    } catch (error: any) {
      console.error('❌ [AuthService] Error during user role lookup:', error);
      
      // Check if this is an HTTP error (login error)
      if (error.isHttpError) {
        console.error('🚫 [AuthService] HTTP error during lookup, signing out user');
        // Clear application state immediately
        this._user.next(null);
        this._idToken.next(null);
        
        // Force sign out from Firebase to prevent partial authentication state
        try {
          await signOut(this.auth);
          console.log('🔓 [AuthService] Successfully signed out user after lookup failure');
        } catch (signOutError) {
          console.error('❌ [AuthService] Error signing out after lookup failure:', signOutError);
        }
        
        // Emit error instead of navigating
        this._authError.next('Unable to verify your account. Please try again.');
        return;
      }
      
      // For other errors (network, server errors), also sign out the user
      // Only allow unregistered flow if we explicitly get 'unregistered' status from backend
      console.error('🚫 [AuthService] Unknown error during lookup, signing out user for security');
      
      // Clear application state
      this._user.next(null);
      this._idToken.next(null);
      
      // Force sign out from Firebase
      try {
        await signOut(this.auth);
        console.log('🔓 [AuthService] Successfully signed out user after unknown error');
      } catch (signOutError) {
        console.error('❌ [AuthService] Error signing out after unknown error:', signOutError);
      }
      
      // Show generic error message
      this._authError.next('Authentication failed. Please try again.');
    }
  }

  get currentUser(): any | null {
    return this._user.value;
  }

  get idToken(): string | null {
    return this._idToken.value;
  }

  get firebaseUser(): any | null {
    return this.auth.currentUser;
  }

  getFirebaseUser(): any | null {
    return this.auth.currentUser;
  }

  updateCurrentUser(userData: any): void {
    this._idToken.next(userData?.idToken || null);
    this._user.next(userData);
  }

  clearAuthError(): void {
    this._authError.next(null);
  }

  get isAuthInitialized(): boolean {
    return this.isInitialized;
  }

  get isAuthenticated(): boolean {
    const user = this.currentUser;
    // User is authenticated if they have a uid and are registered, OR if they have Firebase auth (even if unregistered)
    return !!(user && (user.uid || user.firebaseAuth));
  }

  get isFullyAuthenticated(): boolean {
    const user = this.currentUser;
    // User is fully authenticated only if they have completed both Firebase auth and backend verification
    return !!(user && user.uid && user.registered !== false);
  }

  // Wait for auth to be initialized
  waitForAuthInitialization(): Promise<User | null> {
    if (this.isInitialized) {
      return Promise.resolve(this.currentUser);
    }
    return new Promise((resolve) => {
      const subscription = authState(this.auth).subscribe((user: User | null) => {
        subscription.unsubscribe();
        resolve(user);
      });
    });
  }

  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
      this.router.navigate(['/login']);
      this._idToken.next(null); 
      this._user.next(null);
      this._authError.next(null); // Clear any auth errors
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Method to handle post-login redirect for different login methods
  handlePostLoginRedirect(returnUrl?: string): void {
    this.router.navigate([returnUrl]);
  }

  /**
   * Get user-friendly error message
   */
  getErrorMessage(error: any): string {
    return this.userRoleService.getErrorMessage(error);
  }

  /**
   * Register user with enhanced error handling
   */
  registerUser(userData: any): Observable<any> {
    return this.userRoleService.registerUserWithErrorHandling(userData);
  }

  /**
   * Check if user exists with enhanced error handling
   */
  checkUserExists(mobile?: string, email?: string): Observable<boolean> {
    return this.userRoleService.checkUserExistsWithErrorHandling(mobile, email);
  }
}